import { RESUME, resumeAsJson, skillsList } from "../data/resume";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadResumeJson() {
  const blob = new Blob([resumeAsJson()], { type: "application/json;charset=utf-8" });
  downloadBlob(blob, "Young_He_Resume.json");
}

export async function downloadResumePdf(element) {
  if (!element) return;
  const html2pdf = (await import("html2pdf.js")).default;
  await html2pdf()
    .set({
      margin: [0.45, 0.55, 0.45, 0.55],
      filename: "Young_He_Resume.pdf",
      image: { type: "jpeg", quality: 0.96 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    })
    .from(element)
    .save();
}

export async function downloadResumeDocx() {
  const { Document, HeadingLevel, Packer, Paragraph, TextRun } = await import("docx");
  const { name, headline, contact, summary, experience, education, credentials, skills, selectedWork } = RESUME;

  const contactBits = [
    contact.location,
    contact.openTo,
    contact.email,
    contact.website,
    contact.status,
    contact.agentLabel,
    contact.languages,
  ].filter(Boolean);

  const bulletParagraph = (text) =>
    new Paragraph({
      children: [new TextRun({ text: `• ${text}` })],
      spacing: { after: 120 },
    });

  const children = [
    new Paragraph({
      heading: HeadingLevel.TITLE,
      children: [new TextRun({ text: name, bold: true })],
    }),
    new Paragraph({ children: [new TextRun(headline)] }),
    new Paragraph({
      children: [
        new TextRun(contactBits.join(" · ")),
      ],
      spacing: { after: 240 },
    }),
  ];

  if (summary) {
    children.push(
      new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun("Summary")] }),
      new Paragraph({ children: [new TextRun(summary)], spacing: { after: 240 } })
    );
  }

  children.push(new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun("Experience")] }));

  experience.forEach((job) => {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: job.title, bold: true }),
          new TextRun(`, ${job.company}`),
        ],
        spacing: { before: 180, after: 40 },
      })
    );
    children.push(
      new Paragraph({
        children: [
          new TextRun([job.dates, job.location].filter(Boolean).join(" · ")),
        ],
        spacing: { after: 80 },
      })
    );
    job.bullets.forEach((b) => children.push(bulletParagraph(b)));
  });

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun("Education")], spacing: { before: 240 } })
  );
  education.forEach((ed) => {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: ed.school, bold: true }), new TextRun(`. ${ed.degree} (${ed.dates})`)],
        spacing: { after: 120 },
      })
    );
  });

  if (credentials?.length) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun("Credentials")],
        spacing: { before: 240 },
      }),
      new Paragraph({ children: [new TextRun(credentials.join(" · "))], spacing: { after: 240 } })
    );
  }

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun("Skills")] }),
    new Paragraph({
      children: [new TextRun(skillsList(skills).join(" · "))],
      spacing: { after: 120 },
    })
  );

  if (selectedWork?.length) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun("Selected work")],
        spacing: { before: 240 },
      })
    );
    selectedWork.forEach((w) => {
      children.push(
        new Paragraph({
          children: [new TextRun(`${w.title}. ${w.url}`)],
          spacing: { after: 80 },
        })
      );
    });
  }

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, "Young_He_Resume.docx");
}
